<template>
    <!-- Offline read-only: the store refuses these writes regardless, so the
         control must not look live. A button that appears clickable and does
         nothing is the exact shape that made the site-detail carousel arrows
         the largest source of rageclicks on the site.
         aria-disabled rather than disabled: a disabled button drops out of
         the tab order, so keyboard and screen reader users could not find it
         to learn why it is unavailable. The click is suppressed in
         handleClick instead. -->
    <button
        type="button"
        @click.prevent="handleClick"
        :aria-disabled="!isOnline ? 'true' : undefined"
        :aria-label="hasVisibleLabel ? undefined : label"
        :aria-pressed="isToggle ? String(isPressed) : undefined"
        :aria-describedby="!isOnline ? offlineHintId : undefined"
        :title="!isOnline ? 'Unavailable offline' : undefined"
        :class="{ 'in-favorites': inFavorites, 'in-itinerary': inItinerary, 'is-offline': !isOnline, [`size-${size}`]: true }">
        <component :is="svgName" :size="size" />
        <slot />
        <span v-if="!isOnline" :id="offlineHintId" hidden>Unavailable offline</span>
    </button>
</template>

<script setup>
    // Libraries
    import { storeToRefs } from 'pinia';

    // Stores
    import { useUserStore } from '@/stores/user';
    const userStore = useUserStore();

    // Reactive twin of the store's isOffline() guard — same browser signal.
    const { isOnline } = useOnlineStatus();

    // TO REMOVE:
    const { getOhcList, updateOhcList } = useUtils();

    // Props
    const props = defineProps({
        itinerary: {
            type: Object,
            required: false,
            default: {},
        },
        list: {
            type: String,
            required: true,
            default: '',
        },
        item: {
            type: Object,
            required: false,
            default: {},
        },
        size: {
            type: String,
            required: false,
            default: 'small',
        },
        isGhost: {
            type: Boolean,
            required: false,
            default: false,
        },
    });
    const { isGhost } = props;
    const { itinerary, list, item, size } = toRefs(props);

    // Variables
    const { sites, itineraries, events, tours } = storeToRefs(userStore);
    const { $event } = useNuxtApp();

    // Accessible name. Every state is named for what the button does; offline
    // only adds a description, it never replaces the name.
    const slots = useSlots();
    const hasVisibleLabel = computed(() => !!slots.default);
    const offlineHintId = useId();
    const label = computed(() => userActionLabel(list.value, itemDisplayName(item.value)));
    // Favorites always toggle. The itinerary button only toggles inside an
    // itinerary; elsewhere it opens the "add to which itinerary" modal.
    const isToggle = computed(() => list.value !== 'ohcItineraries' || !!itinerary?.value?.name);
    const isPressed = computed(() => (list.value === 'ohcItineraries' ? inItinerary.value : inFavorites.value));

    // Computed
    const svgName = computed(() => {
        let svgName = '';
        switch (list.value) {
            case 'ohcItineraries':
                svgName = 'SvgsAdd';
                return svgName;
            default:
                svgName = 'SvgsHeart';
                return svgName;
        }
    });

    const inFavorites = computed(() => {
        let found = false;
        const allIds = [...sites.value, ...events.value, ...tours.value];
        if (allIds) {
            allIds.forEach((singleSite) => {
                if (singleSite === item.value.id) {
                    found = true;
                }
            });
        }
        return found;
    });

    const inItinerary = computed(() => {
        let found = false;
        if (itinerary?.value?.sites?.length) {
            itinerary.value.sites.forEach((singleSite) => {
                if (singleSite === item.value.id) {
                    found = true;
                }
            });
        }
        return found;
    });

    // Methods
    function openGlobalAddModal() {
        $event('openGlobalItineraryModal', item);
    }

    function addToItinerary(itemId) {
        if (import.meta.client && itinerary?.value?.name) {
            userStore.addSiteToItinerary(itinerary?.value?.name, itemId);
            // let { ohcList, clone } = getOhcList(list.value);
            // if (ohcList?.length) {
            //   clone.forEach((singleItinerary, index) => {
            //     if (singleItinerary.name === itinerary?.value?.name) {
            //       clone[index].sites.push(itemId);
            //       inItinerary.value = true;
            //       return clone;
            //     }
            //   });
            // }
            // return clone;
        } else {
            // Create ANOTHER layer of pop-up for list of itineraries to add this to.
            openGlobalAddModal();
        }
        return unref(itineraries);
    }

    function handleClick() {
        if (import.meta.client) {
            if (isGhost || !isOnline.value) return;
            // let { ohcList } = getOhcList(list.value);

            switch (list.value) {
                case 'ohcItineraries':
                    if (inItinerary.value) {
                        userStore.removeSiteFromItinerary(props.itinerary.name, item.value.id);
                    } else {
                        addToItinerary(item.value.id);
                        gtmAddedToItinerary(item);
                    }
                    break;

                default:
                    if (inFavorites.value) {
                        userStore.removeSite(item.value.id, list.value);
                        // inFavorites.value = false;
                    } else {
                        userStore.addSite(item.value.id, list.value);
                        // onpmhcList.push(item.value.id);
                        // inFavorites.value = true;

                        gtmFavoritingSite(item);
                    }
                    break;
            }
        }
    }

    function gtmFavoritingSite(item) {
        if (import.meta.client) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'favorited',
                site_name: item?.value?.fields?.Site || 'Name Unavailable',
                site_id: item?.value?.id || 'ID Unavailable',
            });
        }
    }
    function gtmAddedToItinerary(item) {
        if (import.meta.client) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'added_to_itinerary',
                site_name: item?.value?.fields?.Site || 'Name Unavailable',
                site_id: item?.value?.id || 'ID Unavailable',
            });
        }
    }
</script>

<style lang="scss" scoped>
    // Offline read-only. The store refuses the write regardless; this is the
    // half the user can see, so the control reads as unavailable rather than
    // broken.
    button[aria-disabled='true'] {
        opacity: 0.4;
        cursor: not-allowed;
    }

    // The small icon is 34px; pad the hit area out to 44px for thumbs
    // without moving the icon or the layout around it.
    .size-small {
        padding: 5px;
        margin: -5px;
    }
</style>
